import Vue, { provide } from 'vue';
import VueApollo from 'vue-apollo';
import VueMask from 'v-mask';
import defaultProvider, { onLogout } from '@/vue-apollo';
import nexusProvider from '@/apollo/nexusProvider';
import { ValidationObserver, ValidationProvider } from '@/validation';
import axios from 'axios';
import { ApolloClients } from '@vue/apollo-composable';
import {
  getAccessToken,
  getStorageMode,
  isAuthenticated
} from '@/services/secureStorage/secureStorage';
import { MeQuery, AuthRefreshMutation } from '@/generated/nexus-graphql';
import { onNexusLogin } from '@/apollo/nexusAuth';
import { ME_QUERY, REFRESH_MUTATION } from '@/graphql';

import * as Sentry from '@sentry/vue';
import datadogRum from './tracer';

import App from './App.vue';
import DefaultLayout from './layouts/Default.vue';
import NoNavLayout from './layouts/NoNav.vue';
// import './registerServiceWorker';
// eslint-disable-next-line import/no-named-as-default
import router from './router';
import store from './store';
import vuetify from './plugins/vuetify';
import '@mdi/font/css/materialdesignicons.css';

// By default, do NOT send cross-site cookies with every request.
// This avoids CORS "No Allow Credentials" errors when hitting our
// micro-services from different domains. If a specific call really
// needs cookies you can still pass { withCredentials: true } ad-hoc.
axios.defaults.withCredentials = false;

// Helper function to check if URL is a PilotCity service
const isPilotCityService = (url: string): boolean => {
  if (!url) return false;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check for PilotCity domains and localhost (for development)
    return (
      hostname.includes('pilotcity') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.startsWith('192.168.') ||
      hostname.endsWith('.local')
    );
  } catch (error) {
    // If URL parsing fails, fall back to string matching
    return url.toLowerCase().includes('pilotcity');
  }
};

// Add request interceptor to automatically include Bearer token for PilotCity services only
axios.interceptors.request.use(
  function onFulfilled(config) {
    const token = getAccessToken();
    const url = config.url || '';

    // Only add Authorization header for PilotCity services
    if (token && isPilotCityService(url)) {
      return {
        ...config,
        headers: {
          ...(config.headers || {}),
          Authorization: `Bearer ${token}`
        }
      };
    }

    return config;
  },
  function onRejected(error) {
    return Promise.reject(error);
  }
);

console.log(
  `Axios configured with PilotCity domain-restricted token interceptor (Storage mode: ${getStorageMode()})`
);

Sentry.init({
  Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  environment: process.env.NODE_ENV
});
datadogRum.startSessionReplayRecording();

Vue.config.productionTip = false;

Vue.component('DefaultLayout', DefaultLayout);
Vue.component('NoNavLayout', NoNavLayout);
Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);

Vue.use(VueMask);

Vue.use(VueApollo);

axios.interceptors.response.use(
  function onFulfilled(response) {
    return response;
  },
  function onRejected(error) {
    if (error.response?.status === 401) {
      onLogout();
      router.push('/');
    }
    return Promise.reject(error);
  }
);

// Create a combined provider that includes both clients
// Nexus is the default for new functionality, Realm is kept for existing features during migration
const combinedProvider = new VueApollo({
  defaultClient: nexusProvider.defaultClient, // Make Nexus the default for new development
  clients: {
    realm: defaultProvider.defaultClient, // Keep Realm client for existing functionality
    nexus: nexusProvider.defaultClient // Nexus client for new functionality
  }
});

// Bootstrap Vuex user state before the app mounts
async function bootstrapUser() {
  if (isAuthenticated()) {
    try {
      const { data } = await nexusProvider.defaultClient.query<MeQuery>({
        query: ME_QUERY,
        fetchPolicy: 'network-only'
      });
      if (data && data.me) {
        const { me } = data;
        const userPayload = {
          _id: me.dbUser?.id || '',
          userId: me.userId,
          email: me.email,
          firstName: me.firstName,
          lastName: me.lastName,
          fullName: me.fullName,
          profileImg: me.profileImg,
          shareCode: me.shareCode,
          userTypes: me.userTypes || []
        };
        await store.dispatch('user/loginUser', userPayload);
        console.log('Bootstrap user loaded via GraphQL');
      }
    } catch (err) {
      console.warn('Initial Me query failed, attempting token refresh...', err?.message || err);

      // Attempt silent refresh using cookies via authRefresh mutation

      try {
        const { data: refreshData } = await nexusProvider.defaultClient.mutate<AuthRefreshMutation>(
          {
            mutation: REFRESH_MUTATION,
            fetchPolicy: 'no-cache'
          }
        );

        const authRef = refreshData?.authRefresh;
        if (authRef?.ok && authRef.accessToken) {
          // Update Apollo auth header & localStorage
          await onNexusLogin(authRef.accessToken);

          // Retry Me query to hydrate user
          const { data: retryData } = await nexusProvider.defaultClient.query<MeQuery>({
            query: ME_QUERY,
            fetchPolicy: 'network-only'
          });
          if (retryData?.me) {
            const { me } = retryData;
            const userPayload = {
              _id: me.dbUser?.id || me.userId || '',
              userId: me.userId,
              email: me.email,
              firstName: me.firstName,
              lastName: me.lastName,
              fullName: me.fullName,
              profileImg: me.profileImg,
              shareCode: me.shareCode,
              userTypes: me.userTypes || []
            };
            await store.dispatch('user/loginUser', userPayload);
            console.log('Bootstrap user loaded via refreshed token');
            return;
          }
        }
        console.warn('Token refresh failed or returned no token. Logging out.');
        onLogout();
      } catch (refreshErr) {
        console.error('Token refresh mutation failed', refreshErr);
        onLogout();
      }
    }
  }
}

// ----------------------------------------------------------------------------
// Start the application after optional user bootstrap
// ----------------------------------------------------------------------------
(async () => {
  await bootstrapUser();

  const vm = new Vue({
    apolloProvider: combinedProvider,
    setup() {
      // Provide Apollo clients for composition API
      provide(ApolloClients, {
        default: nexusProvider.defaultClient, // Default to Nexus for new functionality
        realm: defaultProvider.defaultClient, // Realm client for existing functionality
        nexus: nexusProvider.defaultClient // Nexus client for new functionality
      });
    },
    router,
    store,
    vuetify,
    render: h => h(App)
  }).$mount('#app');

  // Expose for Cypress tests
  if (typeof window !== 'undefined') {
    (window as any).__app__ = vm;
  }
})();
